import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { DrawerService } from '../../../../core/services/drawer.service';
import {
  map,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
  distinctUntilChanged,
  shareReplay,
  forkJoin,
} from 'rxjs';
import {
  Title,
  UserListModel,
  UserModel,
} from '../../../manage-organization/manage-organization.interface';
import {
  OrganizationDetailModel,
  OrganizationModel,
  userDetailModel,
} from '../../../organizations/organization.interface';
import { Role, RoleService } from '../../../../shared/services/role.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { ManageOrganizationService } from '../../../manage-organization/manage-organization.service';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonService } from '../../../../shared/services/common.service';
import { CommonModule } from '@angular/common';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { UserDrawerParams } from '../user-manage';
import { OrganizationService } from '../../../organizations/organization.service';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { LookupItem } from '../../../../core/models/lookup.models';
import { AuthService } from '../../../../core/services/auth.service';
import { AuditContextService } from '../../../../features/audit-logs/audit-context.service';
import { AuditFieldChange } from '../../../../features/audit-logs/audit-logs.interface';

@Component({
  selector: 'app-edit-user',
  standalone: true,
  imports: [
    NzFormModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzSelectModule,
    NzInputModule,
    NzSwitchModule,
    NzTableModule,
    NzButtonModule,
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    NzSpinModule,
    NzIconModule,
    NzToolTipModule,
    AngularSvgIconModule,
    FormsModule,
  ],
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss',
})
export class EditUserComponent implements OnInit, OnDestroy {
  private readonly drawerService = inject(DrawerService);
  private readonly manageOrgService = inject(ManageOrganizationService);
  private readonly organizationService = inject(OrganizationService);
  private readonly commonService = inject(CommonService);
  private readonly roleService = inject(RoleService);
  private readonly fb = inject(FormBuilder);
  private readonly auditContextService = inject(AuditContextService);
  private readonly destroy$ = new Subject<void>();
  private readonly cdr = inject(ChangeDetectorRef);
  private isTitleLoad = false;

  private isOrgChanging = false;
  private isInitialLoad = true;
  isLoadingUserData = false;

  userDetails = {
    userData: {} as UserListModel,
    orgData: {} as OrganizationDetailModel,
    roleList: [] as Role[],
    mode: 'CREATE',
    from: 'edit-user',
  } as UserDrawerParams;

  _statusList = [
    {
      id: 1,
      value: 'ACTIVE',
    },
    {
      id: 2,
      value: 'INACTIVE',
    },
    {
      id: 3,
      value: 'PENDING',
    },
  ];

  organizations$!: Observable<LookupItem[]>;
  allowedOrganizations$: Observable<OrganizationModel[]> = of([]);
  filteredOrganizations$: Observable<OrganizationModel[]> = of([]);
  searchOrganizationTerm = '';
  titles$!: Observable<Title[]>;
  loading$: Observable<boolean> = of(false);
  mappedRoles: number[] = [];
  userForm!: FormGroup;
  isSubmitting = false;
  DSARoleId = 0;
  cachedAllowedOrganizations: OrganizationModel[] = [];
  cachedTitles: Title[] = [];
  listOfSelection = [
    {
      text: 'Select All Rows',
      onSelect: () => {
        this.onAllChecked(true);
      },
    },
  ];

  checked = false;
  indeterminate = false;
  listOfCurrentPageData: readonly Role[] = [];
  setOfCheckedId = new Set<number>();
  userData: userDetailModel | null = null;
  roleDescriptionMap: Record<string, string> = {};

  readonly authService = inject(AuthService);

  ngOnInit(): void {
    this.loadRoleDescriptions();
    this.authService.userData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.userData = user;
      });
    this.initForm();
    this.getDrawerParams();

    setTimeout(() => {
      this.userForm
        .get('organizationId')
        ?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
        .subscribe(val => {
          // Handle array of organizations
          if (Array.isArray(val)) {
            if (val.length > 0) {
              // Load titles for all organizations in the array
              this.isOrgChanging = true;
              this.loadTitlesForMultipleOrgs(val);
              // Don't clear titleId when organization changes
            } else {
              // Clear titleId only when organizationId becomes empty array
              this.userForm.get('titleId')?.setValue(null);
              this.isInitialLoad = false;
            }
          } else if (typeof val === 'number') {
            // Handle single number value (backward compatibility)
            this.isOrgChanging = true;
            this.loadTitlesForOrg(Number(val));
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRoleDescriptions(): void {
    this.roleService
      .getRoleDescriptions()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: descriptions => {
          this.roleDescriptionMap = descriptions;
        },
        error: () => {
          this.roleDescriptionMap = {};
        },
      });
  }

  getRoleDescription(roleName: string): string {
    return this.roleDescriptionMap[roleName] || '';
  }

  private getContextOrgId(): number {
    const fromUser = this.userDetails?.userData?.organizationId
      ?.split(',')
      .map(id => Number(id.trim()));
    const fromOrgData = this.userDetails?.orgData?.organizationId;
    const fromSelected = this.userDetails?.selectedOrg;
    const id = fromUser ?? [fromOrgData ?? fromSelected ?? 0];
    return Number(id[0]) || 0;
  }

  initForm(): void {
    this.userForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      organizationId: [[], Validators.required],
      titleId: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      statusId: [this.formMode === 'CREATE' ? 3 : null, Validators.required],
    });
  }

  getDrawerParams(): void {
    this.drawerService
      .getParams<UserDrawerParams>(DRAWER_IDS.MANAGE_EDIT_USER)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(params => {
        if (!params) return;
        this.isLoadingUserData = true;
        this.userDetails = params;
        this.loadAllDataAndPopulateForm();
      });
  }

  private loadAllDataAndPopulateForm(): void {
    // Load organizations first
    this.loadAllowedOrganizations();

    // Initialize roles
    this.initializeRoles();
    this.mapUserRolesToChecked();

    // Load titles
    this.loadTitles();

    // Wait for titles to load before populating form in edit mode
    if (this.userDetails.mode === 'EDIT') {
      // Wait for titles$ to be ready
      setTimeout(() => {
        if (this.titles$) {
          this.titles$.pipe(take(1), takeUntil(this.destroy$)).subscribe({
            next: () => {
              this.populateEditModeForm();
              this.refreshCheckedStatus();
              // Set loading to false after a small delay to ensure form is rendered
              setTimeout(() => {
                this.isLoadingUserData = false;
              }, 100);
            },
            error: () => {
              // Hide loader even on error
              this.isLoadingUserData = false;
            },
          });
        } else {
          this.populateEditModeForm();
          this.refreshCheckedStatus();
          this.isLoadingUserData = false;
        }
      }, 100);
    } else {
      // For CREATE mode, no need to wait for form population
      this.refreshCheckedStatus();
      setTimeout(() => {
        this.isLoadingUserData = false;
      }, 300);
    }
  }

  private initializeRoles(): void {
    if (this.userDetails.roleList.length === 0) {
      this.roleService
        .getAllRoles()
        .pipe(takeUntil(this.destroy$))
        .subscribe(response => {
          this.userDetails.roleList = response.result;
          this.resolveDSARoleId();
        });
    }
  }

  private resolveDSARoleId(): void {
    if (this.userDetails.roleList.length > 0) {
      const dsarRole = this.userDetails.roleList.find(
        role => role.name === 'District Settings Admin'
      );
      this.DSARoleId = dsarRole ? dsarRole.id : 0;
    }
  }

  private mapUserRolesToChecked(): void {
    if (!this.userDetails.userData?.roles) return;
    const userRoles = new Set(
      this.userDetails.userData.roles.split(',').map(role => role.trim())
    );
    for (const role of this.userDetails.roleList) {
      if (userRoles.has(role.name)) {
        this.setOfCheckedId.add(role.id);
      }
    }
  }

  private populateEditModeForm(): void {
    if (this.userDetails.mode !== 'EDIT' || !this.userDetails.userData) return;
    this.populateFormFields();
    this.setOrganizationIdInForm();
    this.setTitleIdInForm();
  }

  private populateFormFields(): void {
    const { firstName, lastName } = this.extractNameParts();
    const organizations = this.extractOrganizationIds();
    this.userForm.patchValue({
      firstName,
      lastName,
      email: this.userDetails.userData?.email || '',
      statusId: this.getStatusId(this.userDetails.userData?.status || ''),
      organizationId: organizations.length > 0 ? organizations : [],
    });
  }

  private extractNameParts(): { firstName: string; lastName: string } {
    const name = this.userDetails.userData?.name || '';
    if (!name) return { firstName: '', lastName: '' };
    const nameParts = name.split(' ');
    return {
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
    };
  }

  private extractOrganizationIds(): number[] {
    const orgId = this.userDetails.userData?.organizationId;
    if (!orgId) return [];
    let orgnizations = orgId.split(',').map(id => Number(id.trim()));
    // Filter to keep only valid numbers > 0
    orgnizations = orgnizations.filter(
      (v: number | null | undefined) => typeof v === 'number' && v > 0
    );
    this.allowedOrganizations$.subscribe(response => {
      const allowedOrgIds = new Set(response.map(org => org.id));
      orgnizations = orgnizations.filter(id => allowedOrgIds.has(id));
    });
    return orgnizations;
  }

  private setOrganizationIdInForm(): void {
    const orgId = this.resolveContextOrgIdForForm();
    if (!orgId) return;
    const orgIdArray = this.normalizeToArray(orgId);
    this.userForm.get('organizationId')?.setValue(orgIdArray);
  }

  private resolveContextOrgIdForForm(): string | number | null {
    if (this.userDetails.from === 'user-search') {
      return this.userDetails.userData?.organizationId || null;
    }
    return (
      this.userDetails.orgData?.organizationId ??
      this.userDetails.selectedOrg ??
      null
    );
  }

  private normalizeToArray(orgId: string | number): number[] {
    if (typeof orgId === 'string') {
      return orgId.split(',').map(id => Number(id.trim()));
    }
    return [Number(orgId)];
  }

  private setTitleIdInForm(): void {
    if (!this.userDetails.userData?.title) return;
    this.getTitleId(this.userDetails.userData.title)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(titleId => {
        this.userForm.get('titleId')?.setValue(titleId);
        this.cdr.detectChanges();
      });
  }

  getStatusId(statusValue: string): number {
    const status = this.statusList.find(s => s.value === statusValue);
    return status ? status.id : 0;
  }

  getTitleId(titleValue: string): Observable<number> {
    return this.titles$.pipe(
      map(titles => {
        const normalizedSearch = titleValue.trim().toLowerCase();

        const title = titles.find(
          t => t.title.toLowerCase().trim() === normalizedSearch
        );
        return title ? title.id : 0;
      })
    );
  }

  onTitleChange(titleId: number) {
    if (!titleId || !this.titles$) {
      return;
    }

    this.titles$.pipe(take(1), takeUntil(this.destroy$)).subscribe(titles => {
      if (!titles || titles.length === 0) {
        return;
      }

      const selectedTitle = titles.find(t => t.id === titleId);

      const shouldAutoMapRoles =
        selectedTitle?.isBaseLined && this.userDetails.mode === 'CREATE';

      if (shouldAutoMapRoles) {
        if (this.isTitleLoad && !this.isOrgChanging) {
          this.setOfCheckedId.clear();
        }
        this.loadRolesMappedToTitles(titleId);
        this.isOrgChanging = false;
        this.isInitialLoad = false;
      } else {
        this.refreshCheckedStatus();
        this.isOrgChanging = false;
        this.isInitialLoad = false;
      }
      this.isTitleLoad = true;
    });
  }
  addCheckedId(id: number): void {
    this.setOfCheckedId.add(id);
  }

  removeCheckedId(id: number): void {
    this.setOfCheckedId.delete(id);
  }

  onItemChecked(id: number, checked: boolean): void {
    if (checked) {
      this.addCheckedId(id);
    } else {
      this.removeCheckedId(id);
    }
    this.refreshCheckedStatus();
  }

  onAllChecked(value: boolean): void {
    this.listOfCurrentPageData.forEach(item => {
      if (value) {
        this.addCheckedId(item.id);
      } else {
        this.removeCheckedId(item.id);
      }
    });
    this.refreshCheckedStatus();
  }

  onCurrentPageDataChange($event: readonly Role[]): void {
    this.listOfCurrentPageData = $event;
    this.refreshCheckedStatus();
  }

  refreshCheckedStatus(): void {
    this.checked = this.listOfCurrentPageData.every(item =>
      this.setOfCheckedId.has(item.id)
    );
    this.indeterminate =
      this.listOfCurrentPageData.some(item =>
        this.setOfCheckedId.has(item.id)
      ) && !this.checked;
  }

  onCancel(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_EDIT_USER);
  }

  onSubmit(): void {
    if (!this.validateFormAndRoles()) return;
    const formData = this.userForm.value;
    const resolvedOrgId = this.resolveOrganizationId(formData);
    if (!this.validateOrganization(resolvedOrgId)) return;

    this.isSubmitting = true;
    const selectedOrgIdRaw = formData.organizationId;
    this.resolveParentOrgId(selectedOrgIdRaw, parentOrgId => {
      const payload = this.buildPayload(formData, resolvedOrgId, parentOrgId);
      this.submitUser(payload);
    });
  }

  private validateFormAndRoles(): boolean {
    if (this.userForm.invalid) return false;
    if (this.setOfCheckedId.size === 0) {
      this.commonService.notify('error', 'Please select at least one role.');
      return false;
    }
    return true;
  }

  private resolveOrganizationId(formData: UserModel): number {
    const selectedOrgIdRaw = formData['organizationId'];
    const selectedOrgId = Array.isArray(selectedOrgIdRaw)
      ? selectedOrgIdRaw[0]
      : selectedOrgIdRaw;
    return Number(selectedOrgId || this.getContextOrgId() || 0);
  }

  private validateOrganization(resolvedOrgId: number): boolean {
    if (resolvedOrgId > 0) return true;
    this.isSubmitting = false;
    this.commonService.notify('error', "Please select user's School.");
    return false;
  }

  private resolveParentOrgId(
    selectedOrgIdRaw: number | number[],
    callback: (parentOrgId: number | null) => void
  ): void {
    const roleIds = Array.from(this.setOfCheckedId);
    if (!roleIds.includes(this.DSARoleId) || roleIds.length === 1) {
      callback(null);
      return;
    }
    this.allowedOrganizations$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(orgs => {
        const selectedOrgId = Array.isArray(selectedOrgIdRaw)
          ? selectedOrgIdRaw[0]
          : selectedOrgIdRaw;
        const selectedOrg = orgs.find(o => o.id === selectedOrgId);
        callback(selectedOrg?.parentOrganizationId ?? null);
      });
  }

  private buildPayload(
    formData: UserModel,
    resolvedOrgId: number,
    parentOrgId: number | null
  ): UserModel {
    const payload: UserModel = {
      id: this.userDetails.userData?.id,
      organizationId: resolvedOrgId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      titleId: formData.titleId,
      statusId: formData.statusId,
      roleIds: Array.from(this.setOfCheckedId),
      isAllUsers: this.userDetails.from === 'user-search',
      isParentOrg: this.resolveIsParentOrg(),
    };
    payload.organizationIds = this.constructOrganizationIds(
      formData,
      parentOrgId
    );

    // Build enriched data with display values and track field changes for audit logging
    const enrichedPayload = this.buildEnrichedPayload(formData, resolvedOrgId);
    payload._modifiedFields = enrichedPayload.modifiedFields;

    return payload;
  }

  private resolveIsParentOrg(): number {
    if (this.userDetails.from !== 'user-search') {
      return (
        this.userDetails.orgData?.organizationId ??
        this.userDetails.selectedOrg ??
        0
      );
    }
    const orgIds = this.getExistingOrgIds();
    return orgIds.length > 1 ? orgIds[0] : 0;
  }

  private buildEnrichedPayload(
    formData: UserModel,
    resolvedOrgId: number
  ): { modifiedFields: AuditFieldChange[] } {
    const isEditMode = this.userDetails.mode === 'EDIT';
    const original = isEditMode ? this.extractOriginalUserData() : {};
    const updated = this.buildUpdatedUserData(formData, resolvedOrgId);

    // Handle multiple organization IDs - convert to comma-separated names
    const orgIdArray = Array.isArray(formData['organizationId'])
      ? formData['organizationId']
      : [];
    if (orgIdArray.length > 0) {
      this.allowedOrganizations$.pipe(take(1)).subscribe(orgs => {
        const orgNames = orgIdArray
          .map((orgId: number) => {
            const org = orgs.find(o => o.id === orgId);
            return org?.organizationName || '';
          })
          .filter((name: string) => name.length > 0)
          .join(', ');
        updated['organizationName'] = orgNames;
      });
    }

    const modifiedFields = this.auditContextService.buildFieldChanges(
      original,
      updated,
      {
        sensitiveFields: ['email', 'password'],
        labelMap: this.buildFieldLabelMap(),
        excludeFields: [
          'id',
          'createdDate',
          'updatedDate',
          'createdBy',
          'updatedBy',
          'isAllUsers',
          'isParentOrg',
        ],
      }
    );

    return { modifiedFields };
  }

  private extractOriginalUserData(): Record<string, unknown> {
    if (!this.userDetails.userData) return {};

    return {
      firstName: this.userDetails.userData.name?.split(' ')[0] || '',
      lastName:
        this.userDetails.userData.name?.split(' ').slice(1).join(' ') || '',
      email: this.userDetails.userData.email || '',
      statusName: this.userDetails.userData.status || '',
      organizationName: this.userDetails.userData.organizationName || '',
      titleName: this.userDetails.userData.title || '',
      roleNames: this.userDetails.userData.roles || '',
    };
  }

  private buildUpdatedUserData(
    formData: UserModel,
    resolvedOrgId: number
  ): Record<string, unknown> {
    return {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      statusName: this.getStatusName(formData.statusId),
      organizationName: this.getOrganizationName(resolvedOrgId),
      titleName: this.getTitleName(formData.titleId),
      roleNames: this.getRoleNames(Array.from(this.setOfCheckedId)),
    };
  }

  private getStatusName(statusId: number): string {
    const status = this._statusList.find(s => s.id === statusId);
    return status?.value || '';
  }

  private getOrganizationName(orgId: number): string {
    const org = this.cachedAllowedOrganizations.find(o => o.id === orgId);
    return org?.organizationName || '';
  }

  private getTitleName(titleId: number): string {
    const title = this.cachedTitles.find(t => t.id === titleId);
    return title?.title || '';
  }

  private getRoleNames(roleIds: number[]): string {
    // Deduplicate role names to avoid repeated roles in audit log
    const names = this.userDetails.roleList
      .filter(role => roleIds.includes(role.id))
      .map(role => role.name);
    return Array.from(new Set(names)).join(', ');
  }

  private buildFieldLabelMap(): Record<string, string> {
    return {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email Address',
      statusName: 'Status',
      organizationName: 'Organization',
      titleName: 'Title',
      roleNames: 'Roles',
    };
  }

  private getExistingOrgIds(): number[] {
    const orgId = this.userDetails.userData?.organizationId;
    if (!orgId) return [];
    return orgId.split(',').map(id => Number(id.trim()));
  }

  private constructOrganizationIds(
    formData: UserModel,
    parentOrgId: number | null
  ): number[] {
    const orgIdArray = Array.isArray(formData['organizationId'])
      ? formData['organizationId']
      : [formData['organizationId']].filter(
          (v: number | null | undefined) => typeof v === 'number' && v > 0
        );
    if (!parentOrgId || parentOrgId <= 0) return orgIdArray;
    return [...orgIdArray, parentOrgId];
  }

  private submitUser(payload: UserModel): void {
    const isEditMode =
      this.userDetails.mode === 'EDIT' && !!this.userDetails.userData?.id;
    const submitService$ = isEditMode
      ? this.manageOrgService.editOrgUser(payload, this.userDetails.pageIndex)
      : this.manageOrgService.createOrgUser(
          payload,
          this.userDetails.pageIndex
        );

    submitService$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.handleSubmitSuccess(isEditMode),
      error: error => this.handleSubmitError(error, isEditMode),
    });
  }

  private handleSubmitSuccess(isEditMode: boolean): void {
    this.isSubmitting = false;
    const message = isEditMode
      ? 'User updated successfully.'
      : 'User created successfully.';
    this.commonService.notify('success', message);
    this.drawerService.closeAllDrawers();
  }

  private handleSubmitError(error: Error, isEditMode: boolean): void {
    this.isSubmitting = false;
    const message = isEditMode ? 'Error updating user' : 'Error creating user';
    this.commonService.notify('error', error.message || message);
  }

  loadTitles(): void {
    const orgId = this.getContextOrgId();
    this.loadTitlesForOrg(orgId);
  }

  private loadTitlesForOrg(orgId: number): void {
    this.titles$ = this.manageOrgService
      .getTitles({ organizationId: orgId })
      .pipe(
        map(response => {
          this.cachedTitles = response.result || [];
          return this.cachedTitles;
        }),
        shareReplay(1),
        takeUntil(this.destroy$)
      );
  }

  private loadTitlesForMultipleOrgs(orgIds: number[]): void {
    const titleRequests = orgIds.map(orgId =>
      this.manageOrgService
        .getTitles({ organizationId: Number(orgId) })
        .pipe(map(response => response.result || []))
    );

    this.titles$ = forkJoin(titleRequests).pipe(
      map(titlesArrays => {
        const allTitles = titlesArrays.flat();
        const uniqueTitles = allTitles.filter(
          (title, index, self) =>
            index === self.findIndex(t => t.id === title.id)
        );
        this.cachedTitles = uniqueTitles;
        return uniqueTitles;
      }),
      shareReplay(1),
      takeUntil(this.destroy$)
    );
  }
  loadRolesMappedToTitles(titleId: number): void {
    this.manageOrgService
      .getMappedRolesToTitle(titleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          if (res.result) {
            this.mappedRoles = res.result;
            if (this.userDetails.mode === 'CREATE') {
              this.userDetails.roleList.forEach(role => {
                if (this.mappedRoles.includes(role.id)) {
                  this.setOfCheckedId.add(role.id);
                }
              });
              this.setOfCheckedId = new Set(this.setOfCheckedId);
              this.cdr.detectChanges();
            }
          }
          this.refreshCheckedStatus();
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  initAllowedOrganizations(): void {
    this.loadAllowedOrganizations();
  }

  loadAllowedOrganizations(): void {
    if (this.userData?.isSuperAdmin) {
      this.allowedOrganizations$ = this.organizationService
        .getOrganizationsByUserId(this.userDetails.userData?.id)
        .pipe(
          map(response => {
            const organizations = response?.result || [];
            this.cachedAllowedOrganizations = Array.isArray(organizations)
              ? organizations
              : [];
            return this.cachedAllowedOrganizations;
          }),
          shareReplay(1),
          takeUntil(this.destroy$)
        );

      this.filteredOrganizations$ = this.allowedOrganizations$;
    } else {
      this.allowedOrganizations$ = this.organizationService
        .getOrganizationsToSwitch()
        .pipe(
          map(response => {
            const organizations = response?.result || [];
            this.cachedAllowedOrganizations = Array.isArray(organizations)
              ? organizations
              : [];
            return this.cachedAllowedOrganizations;
          }),
          shareReplay(1),
          takeUntil(this.destroy$)
        );

      this.filteredOrganizations$ = this.allowedOrganizations$;
    }
  }

  onOrganizationSearch(searchValue: string): void {
    this.searchOrganizationTerm = searchValue;

    if (searchValue.trim()) {
      this.filteredOrganizations$ = this.allowedOrganizations$.pipe(
        map(orgs =>
          orgs.filter(org =>
            org.organizationName
              .toLowerCase()
              .includes(searchValue.toLowerCase())
          )
        )
      );
    } else {
      this.filteredOrganizations$ = this.allowedOrganizations$;
    }
  }

  get formMode(): string {
    return this.userDetails.mode;
  }

  get statusList() {
    if (this.formMode === 'CREATE') {
      return this._statusList.filter(status => status.value !== 'ACTIVE');
    }
    return this._statusList;
  }

  get isActive(): boolean {
    return this.userForm.get('statusId')?.value === 1;
  }

  setActiveStatus(active: boolean): void {
    this.userForm.get('statusId')?.setValue(active ? 1 : 2);
  }
}
